// ============================================================
// Session Timeout Modal
// ============================================================
// Displayed when the auth token expires (401 response).
// This modal is strict & non-closable — the user can only click "Logout"
// which clears session state and navigates to the login page.

import { Dialog, DialogContent, Typography, Button, Box } from '@mui/material';
import { Clock, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '@/store/authStore';
import { useAuth } from '@/hooks/useAuth';
import { tokens } from '@/theme/tokens';

export function SessionTimeoutModal() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isSessionExpired = useAuthStore((state) => state.isSessionExpired);
  const setSessionExpired = useAuthStore((state) => state.setSessionExpired);

  const handleLogout = async () => {
    setSessionExpired(false);
    try {
      await logout();
    } catch (error) {
      console.error('Error during timeout logout:', error);
    } finally {
      navigate('/login', { replace: true });
    }
  };

  return (
    <Dialog
      open={Boolean(isSessionExpired)}
      onClose={() => {
        // Prevent closing via backdrop click or keyboard
      }}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          p: 1.5,
          boxShadow: '0 24px 48px -12px rgba(15, 23, 42, 0.25)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          py: 4,
          px: 3,
        }}
      >
        {/* Icon Header */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: `${tokens.colors.warning?.main || '#f59e0b'}15`,
            color: tokens.colors.warning?.main || '#f59e0b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2.5,
            position: 'relative',
            boxShadow: `0 0 0 8px ${tokens.colors.warning?.main || '#f59e0b'}0A`,
          }}
        >
          <Clock size={32} strokeWidth={2.2} />
        </Box>

        {/* Title */}
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{
            color: tokens.colors.text?.primary || '#0f172a',
            mb: 1,
            letterSpacing: '-0.01em',
          }}
        >
          Session Timed Out
        </Typography>

        {/* Body Text */}
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{
            mb: 4,
            lineHeight: 1.5,
            fontSize: '0.95rem',
            maxWidth: 320,
          }}
        >
          Your session has timed out. Please login again.
        </Typography>

        {/* Logout Button */}
        <Button
          variant="contained"
          color="error"
          fullWidth
          size="large"
          onClick={handleLogout}
          startIcon={<LogOut size={20} />}
          sx={{
            py: 1.4,
            fontSize: '0.95rem',
            fontWeight: 600,
            borderRadius: 2.5,
            textTransform: 'none',
            boxShadow: '0 8px 20px -4px rgba(239, 68, 68, 0.4)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 12px 24px -4px rgba(239, 68, 68, 0.5)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Logout
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default SessionTimeoutModal;
